import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import AvatarEditor from 'react-avatar-editor';
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

interface ProfileImageModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onImageUploaded: (url: string) => void;
}

const ProfileImageModal = ({ isOpen, onOpenChange, onImageUploaded }: ProfileImageModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [zoom, setZoom] = useState(1.2);
  const [isUploading, setIsUploading] = useState(false);
  const editorRef = useRef<AvatarEditor>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSave = async () => {
    if (!editorRef.current || !selectedFile) return;

    try {
      setIsUploading(true);
      
      // Get the edited canvas data
      const canvas = editorRef.current.getImageScaledToCanvas();
      const blob = await new Promise<Blob>((resolve) => 
        canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.95)
      );
      
      const file = new File([blob], 'profile-image.jpg', { type: 'image/jpeg' });
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Upload to Supabase Storage
      const fileName = `${user.id}/${Date.now()}.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(fileName);

      // Update profile with new image URL
      const { error: updateError } = await supabase
        .from('developer_profiles')
        .upsert({
          user_id: user.id,
          profile_image_url: publicUrl,
          updated_at: new Date().toISOString(),
        },{ onConflict: 'user_id' })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      onImageUploaded(publicUrl);
      onOpenChange(false);
      toast({
        title: "Success",
        description: "Profile image updated successfully",
      });

    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Profile Image</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {!selectedFile ? (
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
            />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <AvatarEditor
                  ref={editorRef}
                  image={selectedFile}
                  width={250}
                  height={250}
                  border={50}
                  borderRadius={125}
                  color={[0, 0, 0, 0.6]}
                  scale={zoom}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Zoom</Label>
                <Slider
                  value={[zoom]}
                  onValueChange={([value]) => setZoom(value)}
                  min={1}
                  max={3}
                  step={0.1}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setSelectedFile(null);
              onOpenChange(false);
            }}
            disabled={isUploading}
          >
            Cancel
          </Button>
          {selectedFile && (
            <Button
              type="submit"
              onClick={handleSave}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileImageModal;